import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts/enums';
import { ArrowLeftRight, Plus, Filter, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import useGlobalProgressStore from '@/store/GlobalProgressStore';
import { useGetFilters } from '@/reactQuery/get';
import { useListComparisons, useCompareSegments } from '@/reactQuery/comparison';
import EditFilterModal from '@/components/Modals/FilterModal/EditFilterModal';
import ComparisonCard from './ComparisonCard';
import CompareFiltersModal from './CompareFiltersModal';

interface ComparisonHubProps {
  analysisLanguage: string;
  onReRun?: (filterId?: string | null) => Promise<void>;
  isRerunLoading?: boolean;
}

function ComparisonHub({ analysisLanguage, onReRun, isRerunLoading }: ComparisonHubProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [, setSearchParams] = useSearchParams();
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showCreateFilterModal, setShowCreateFilterModal] = useState(false);
  const [generatingProgress, setGeneratingProgress] = useState('');
  const queryClient = useQueryClient();

  // useGetFilters is typed as FiltersDataToSendProps[] (the create-filter
  // request shape), but the API actually returns each row with `_id` and
  // `name` fields. The existing convention (see DownloadAnalysisButton.tsx)
  // is to cast at the consumer site rather than widen the request type.
  const { data: filtersRaw, isLoading: isLoadingFilters, refetch: refetchFilters } = useGetFilters(projectId);
  const filters = filtersRaw as unknown as Array<{ _id: string; name: string }> | undefined;
  const { data: comparisons, isLoading: isLoadingComparisons } = useListComparisons(projectId);
  const { mutateAsync: compareSegments, isPending: isComparing, abort: abortComparison } = useCompareSegments();
  const [comparisonStartedAt, setComparisonStartedAt] = useState<number | null>(null);

  const hasEnoughFilters = (filters?.length ?? 0) >= 2;

  const handleCompare = async (filterIdA: string, filterIdB: string) => {
    setShowCompareModal(false);
    setComparisonStartedAt(Date.now());
    setGeneratingProgress(lang.get('msg.comparisonStarting'));
    const progressId = `comparison-${filterIdA}-${filterIdB}`;
    useGlobalProgressStore.getState().addEntry({
      id: progressId,
      type: 'comparison',
      label: lang.get('msg.comparisonGenerating'),
      startedAt: Date.now(),
      projectId: projectId!,
      returnUrl: `${window.location.pathname}${window.location.search}`,
    });
    try {
      const { comparisonId } = await compareSegments({
        projectId: projectId!,
        filterIdA,
        filterIdB,
        onProgress: setGeneratingProgress,
      });
      setGeneratingProgress('');
      setComparisonStartedAt(null);
      useGlobalProgressStore.getState().removeEntry(progressId);
      // Navigate to the report
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(SearchParam.COMPARISON_ID, comparisonId);
        return next;
      });
    } catch (error: unknown) {
      setGeneratingProgress('');
      setComparisonStartedAt(null);
      useGlobalProgressStore.getState().removeEntry(progressId);
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { code?: string; message?: string };
        };
        message?: string;
      };
      // Backend returns 400 with `{ code: 'RA_ANALYSIS_OUTDATED', message }`
      // when one of the compared filters has a stale overall_pages doc
      // (Research Assistant rework B1.b gate). We invalidate the
      // schema-status cache so the parent CompareTab refetches and
      // re-shows the OutdatedAnalysisDialog (B1.f). The toast is a
      // visible fallback in case the dialog has already been dismissed.
      if (axiosError?.response?.data?.code === 'SEGMENT_EMPTY') {
        toast.error(lang.get('msg.comparisonSegmentEmpty'));
      } else if (axiosError?.response?.data?.code === 'ANALYSIS_IN_PROGRESS') {
        toast.error(lang.get('msg.analysisInProgressCompare'));
      } else if (axiosError?.response?.data?.code === 'RA_ANALYSIS_OUTDATED') {
        queryClient.invalidateQueries({
          queryKey: ['ra-analysis-schema-status'],
        });
        toast.error(lang.get('msg.analysisOutdatedForCompare'));
      } else if (axiosError?.response?.status === 404) {
        toast.error(lang.get('msg.comparisonRequiresAnalysis'));
      } else {
        toast.error(lang.get('msg.comparisonFailed'));
      }
    }
  };

  const handleViewComparison = (comparisonId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(SearchParam.COMPARISON_ID, comparisonId);
      return next;
    });
  };

  // Loading state while filters and comparisons load
  if (isLoadingFilters || isLoadingComparisons) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        </div>
      </div>
    );
  }

  // Inline progress during generation
  if (isComparing || generatingProgress) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-col items-center justify-center gap-6 py-16 rounded-xl border border-neutral-200 bg-white">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div className="text-center">
            <p className="text-sm font-medium text-neutral-700">
              {generatingProgress || lang.get('msg.comparisonGenerating')}
            </p>
            {comparisonStartedAt && (
              <ElapsedTimer startedAt={comparisonStartedAt} />
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              abortComparison();
              setGeneratingProgress('');
              setComparisonStartedAt(null);
            }}
            className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            {lang.get('msg.cancel')}
          </button>
        </div>
      </div>
    );
  }

  // Empty state: not enough filters
  if (!hasEnoughFilters) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-neutral-100">
            <Filter className="h-6 w-6 text-neutral-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-neutral-900 mb-1">
              {lang.get('msg.createAudienceFiltersFirst')}
            </h3>
            <p className="text-sm text-neutral-500 max-w-md mb-4">
              {lang.get('msg.createAudienceFiltersFirstDescription')}
            </p>
          </div>
          <Button onClick={() => setShowCreateFilterModal(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {lang.get('msg.addFilter')}
          </Button>
        </div>

        {showCreateFilterModal && (
          <EditFilterModal
            project_id={projectId}
            setOpenEditModal={setShowCreateFilterModal}
            onReRun={onReRun!}
            editFilterData={null}
            refetch={refetchFilters}
          />
        )}
      </div>
    );
  }

  // Empty state: no comparisons yet
  if (!isLoadingComparisons && (!comparisons || comparisons.length === 0)) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-emerald-50">
            <ArrowLeftRight className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-neutral-900 mb-1">
              {lang.get('msg.compareAudienceSegments')}
            </h3>
            <p className="text-sm text-neutral-500 max-w-md mb-4">
              {lang.get('msg.compareAudienceSegmentsDescription')}
            </p>
          </div>
          <Button onClick={() => setShowCompareModal(true)} disabled={isRerunLoading} className="gap-2">
            <Plus className="h-4 w-4" />
            {lang.get('msg.newComparison')}
          </Button>
        </div>

        {showCompareModal && (
          <CompareFiltersModal
            filters={filters || []}
            isOpen={showCompareModal}
            onClose={() => setShowCompareModal(false)}
            onCompare={handleCompare}
            isLoading={isComparing}
            analysisLanguage={analysisLanguage}
          />
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">
            {lang.get('msg.compareSegments')}
          </h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            {lang.get('msg.compareSegmentsDescription')}
          </p>
        </div>
        <Button onClick={() => setShowCompareModal(true)} disabled={isRerunLoading} size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          {lang.get('msg.newComparison')}
        </Button>
      </div>

      {/* Comparison list */}
      <div className="flex flex-col gap-3">
        {comparisons?.map((comparison) => (
          <ComparisonCard
            key={comparison._id}
            comparison={comparison}
            onView={() => handleViewComparison(comparison._id)}
            onRegenerate={handleCompare}
            isRegenerating={isComparing}
          />
        ))}
      </div>

      {showCompareModal && (
        <CompareFiltersModal
          filters={filters || []}
          isOpen={showCompareModal}
          onClose={() => setShowCompareModal(false)}
          onCompare={handleCompare}
          isLoading={isComparing}
          analysisLanguage={analysisLanguage}
        />
      )}
    </div>
  );
}

/** Ticking elapsed timer that updates every second. */
function ElapsedTimer({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    intervalRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [startedAt]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;

  return (
    <p className="text-xs text-neutral-400 mt-1 tabular-nums">
      {mins}:{secs.toString().padStart(2, '0')}
    </p>
  );
}

export default ComparisonHub;
